import { useMemo } from 'react';
import type { Member } from '@court-sync/shared';
import {
  PaginatedTable,
  type PaginatedTableColumn,
  type PaginationState,
  Button,
  Badge,
} from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { UserAvatar } from '@/components/UserAvatar';
import { cn } from '@/utils/cn';
import { MoreVertical, Pencil } from 'lucide-react';

const MEMBERSHIP_STATUS_LABELS: Record<string, string> = {
  pending_payment: 'Pending payment',
  pending_verification: 'Pending verification',
  active: 'Active',
  past_due: 'Past due',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

const MEMBERSHIP_STATUS_BADGE_VARIANT: Record<
  string,
  'success' | 'warning' | 'secondary' | 'destructive'
> = {
  pending_payment: 'secondary',
  pending_verification: 'warning',
  active: 'success',
  past_due: 'warning',
  expired: 'secondary',
  cancelled: 'destructive',
};

function memberMembershipStatusBadge(member: Member) {
  const status = member.currentMembershipStatus ?? null;
  if (!status) {
    return <Badge variant="secondary">None</Badge>;
  }
  return (
    <Badge variant={MEMBERSHIP_STATUS_BADGE_VARIANT[status] ?? 'secondary'}>
      {MEMBERSHIP_STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

export interface ClubMembersTableProps {
  members: Member[];
  pagination: PaginationState | null;
  loading?: boolean;
  emptyMessage?: string;
  onPageChange: (page: number) => void;
  householdLabel: (householdId: string | null | undefined) => string;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: () => void;
  onEditMember: (member: Member) => void;
  onArchiveMember: (member: Member) => void;
  onReactivateMember: (member: Member) => void;
}

export function ClubMembersTable({
  members,
  pagination,
  loading = false,
  emptyMessage = 'No members found.',
  onPageChange,
  householdLabel,
  selectedIds = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  onEditMember,
  onArchiveMember,
  onReactivateMember,
}: ClubMembersTableProps) {
  const bulkEnabled = Boolean(onToggleSelect && onToggleSelectAll);

  const columns: PaginatedTableColumn<Member>[] = useMemo(() => {
    const cols: PaginatedTableColumn<Member>[] = [];

    if (bulkEnabled) {
      cols.push({
        key: 'select',
        header: (
          <div className="flex justify-center">
            <input
              type="checkbox"
              aria-label="Select all on this page"
              checked={members.length > 0 && members.every((m) => selectedIds.has(m.id))}
              onChange={onToggleSelectAll}
              className="rounded border-gray-300"
            />
          </div>
        ),
        className: 'w-10 align-middle',
        render: (member) => (
          <div className="flex justify-center">
            <input
              type="checkbox"
              checked={selectedIds.has(member.id)}
              onChange={() => onToggleSelect!(member.id)}
              aria-label={`Select ${member.firstName} ${member.lastName}`}
              className="rounded border-gray-300"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ),
      });
    }

    cols.push(
      {
        key: 'name',
        header: 'Name',
        render: (member) => (
          <div className="flex items-center gap-3">
            <UserAvatar
              avatarUrl={member.avatarUrl}
              firstName={member.firstName}
              lastName={member.lastName}
              size="sm"
            />
            <div>
              <p
                className={cn(
                  'font-medium',
                  member.isActive ? 'text-gray-900 dark:text-white' : 'text-muted-foreground'
                )}
              >
                {member.firstName} {member.lastName}
              </p>
              {member.isActive ? null : (
                <p className="text-xs text-muted-foreground">Archived</p>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'email',
        header: 'Email',
        render: (member) =>
          member.email ? (
            <a href={`mailto:${member.email}`} className="text-primary hover:underline">
              {member.email}
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'phone',
        header: 'Phone',
        render: (member) =>
          member.phone ? (
            <a href={`tel:${member.phone}`} className="text-primary hover:underline">
              {member.phone}
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'household',
        header: 'Household',
        render: (member) => householdLabel(member.householdId),
      },
      {
        key: 'membership',
        header: 'Membership',
        render: (member) => memberMembershipStatusBadge(member),
      },
      {
        key: 'actions',
        header: <span className="sr-only">Actions</span>,
        className: 'text-right',
        render: (member) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${member.firstName} ${member.lastName}`}
              >
                <MoreVertical className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onEditMember(member)}>
                <Pencil className="h-4 w-4" aria-hidden />
                Edit
              </DropdownMenuItem>
              {member.isActive ? (
                <DropdownMenuItem onSelect={() => onArchiveMember(member)}>
                  Archive
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => onReactivateMember(member)}>
                  Restore
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      }
    );

    return cols;
  }, [
    bulkEnabled,
    members,
    selectedIds,
    onToggleSelect,
    onToggleSelectAll,
    householdLabel,
    onEditMember,
    onArchiveMember,
    onReactivateMember,
  ]);

  return (
    <PaginatedTable<Member>
      columns={columns}
      data={members}
      pagination={pagination}
      loading={loading}
      emptyMessage={emptyMessage}
      onPageChange={onPageChange}
      noWrapperPadding
    />
  );
}
